import { AgGridReact } from "ag-grid-react";
import { memo, useCallback } from "react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import "../../assets/css/AGGrid-custom.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { useDealProvider } from "../../context/DealContext";
import { renderSubTitle } from "../../components/renderComponents";
import { useEffect, useRef, useMemo, useState } from "react";
import { apiGet } from "../../services/genesisApi/httpsMethods";
import { numberFormatter, dateFormatter } from "../../utils/numberFormatter";
import { Box, IconButton } from "@mui/material";
import { CustomButton } from "../../components/common/customButton";
import {
  createObjectArraySelectColumn,
  createSingleSelectColumn,
  createStringSelectColumn,
} from "../../utils/columnHelpers";
import { useReferenceLists } from "../../context/GlobalGetLists";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Accordion } from "../../types/deal";

ModuleRegistry.registerModules([AllCommunityModule]);

const ExtractedAssetsTable = ({ dealId }: { dealId: number | null }) => {
  const { assets, setAssets, setTenants } = useDealProvider();
  const { countries, sectors, deals, currencies } = useReferenceLists();
  const gridRef = useRef<any>(null);
  const [accordions, setAccordions] = useState<Accordion[]>([]);
  const [accordionAssignments, setAccordionAssignments] = useState<
    Map<number, number | null>
  >(new Map()); // Track asset -> accordion ID (single accordion per asset)

  // Determine context: Edit Deal (has dealId) vs Upload/Create Asset (no dealId)
  const isEditDealContext = !!dealId;

  useEffect(() => {
    if (dealId) {
      apiGet(`assets?dealId=${dealId}`)
        .then((response) => {
          if (response && response.success && response.data) {
            // Ensure each asset has a unique ID (for extracted assets that don't have database IDs yet)
            const assetsWithIds = response.data.map(
              (asset: any, index: number) => ({
                ...asset,
                id:
                  asset.id ||
                  `temp-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
              }),
            );

            // Merge with existing assets instead of replacing
            // This preserves extraction assets that haven't been saved to DB yet

            setAssets((prevAssets) => {
              // If no existing assets, just set the fetched ones
              if (!prevAssets || prevAssets.length === 0) {
                return assetsWithIds || [];
              }

              const newArray = prevAssets.map((a: any) => {
                const found = assetsWithIds.find(
                  (p: any) => p.asset_id === a.id,
                );

                if (found) {
                  return {
                    ...found,
                    id: found.asset_id,
                  };
                }

                return { ...a };
              });

              return [...newArray];
            });

            // Force grid refresh after data is loaded
            setTimeout(() => {
              if (gridRef.current) {
                gridRef.current.api.refreshCells({ force: true });
              }
            }, 100);
          }
        })
        .catch((err) => console.error("Failed to fetch assets", err));
    }
  }, [dealId, setAssets]);

  // Fetch accordions for the deal
  useEffect(() => {
    const fetchAccordions = async () => {
      if (!dealId) {
        setAccordions([]);
        return;
      }

      try {
        const response = await apiGet(`deals/${dealId}/accordions`);
        if (response.success && response.data) {
          const fetchedAccordions = Array.isArray(response.data)
            ? response.data
            : [];
          setAccordions(fetchedAccordions);

          // Don't overwrite accordion assignments - they're initialized from assets
          // Only initialize if assignments map is empty (fallback for when assets haven't loaded yet)
          // This prevents overwriting user changes
          setAccordionAssignments((prev) => {
            if (prev.size > 0) {
              // Keep existing assignments from assets (including null assignments)
              return prev;
            }
            // Fallback: initialize from accordion data if no assets loaded yet
            const assignments = new Map<number, number | null>();
            fetchedAccordions.forEach((accordion: Accordion) => {
              if (accordion.asset_ids && Array.isArray(accordion.asset_ids)) {
                accordion.asset_ids.forEach((assetId: number) => {
                  if (!assignments.has(assetId)) {
                    assignments.set(assetId, accordion.id);
                  }
                });
              }
            });
            return assignments;
          });
        }
      } catch (error) {
        console.error("Failed to fetch accordions:", error);
        setAccordions([]);
      }
    };

    fetchAccordions();
  }, [dealId]);

  // Update accordion assignments when assets change
  useEffect(() => {
    if (assets && assets.length > 0) {
      setAccordionAssignments((prev) => {
        const updated = new Map(prev);
        assets.forEach((asset: any) => {
          if (
            asset.id &&
            typeof asset.id === "number" &&
            asset.accordion_id !== undefined
          ) {
            updated.set(asset.id, asset.accordion_id || null);
          }
        });
        return updated;
      });
    }
  }, [assets]);

  // Filter out "Initial Deal" accordion for selection
  const selectableAccordions = useMemo(() => {
    return accordions.filter((acc) => acc.accordion_name !== "Initial Deal");
  }, [accordions]);

  const handleCheckboxChange = (checked: boolean, assetId: number) => {
    if (isEditDealContext) {
      const updatedAssets = assets.map((asset: any) => {
        if (asset.id === assetId) {
          return {
            ...asset,
            deal: checked ? { id: dealId! } : null,
          };
        }
        return asset;
      });
      setAssets(updatedAssets as any);
      gridRef.current?.api.refreshCells({ force: true, suppressFlash: true });
      return;
    }

    // Create: rows in the table are saved. Uncheck removes the asset (no shouldCreate flag).
    if (!checked) {
      setTenants((prev) =>
        (prev as any[]).filter(
          (tenant: any) =>
            tenant?.asset?.id !== assetId && tenant?.asset_id !== assetId,
        ) as any,
      );
      setAssets(
        (prev) =>
          (prev as any[]).filter((asset: any) => asset?.id !== assetId) as any,
      );
    }
  };

  const handleDeleteRowById = useCallback(
    async (rowId: number | string): Promise<void> => {
      if (rowId === null || rowId === undefined) return;

      // If in edit deal context and asset has a database ID, detach it from the deal via API
      if (isEditDealContext && dealId && typeof rowId === "number") {
        if (
          !confirm("Are you sure you want to detach this asset from the deal?")
        ) {
          return;
        }

        try {
          // Update asset to remove deal_id

          // Remove asset from local state

          // Filter by rowId directly (not via a lookup in a possibly stale `assets`
          // closure) and match both nested asset and asset_id links.
          setTenants((prev) =>
            prev.filter(
              (tt: any) => tt.asset?.id !== rowId && tt.asset_id !== rowId,
            ),
          );

          setAssets(
            (prev) =>
              (prev as any[]).filter((a: any) => a?.id !== rowId) as any,
          );
        } catch (error) {
          console.error("Error detaching asset from deal:", error);
          alert("Failed to detach asset from deal. Please try again.");
        }
      } else {
        // Just remove from local state (for new assets that haven't been saved yet)
        setAssets(
          (prev) => (prev as any[]).filter((a: any) => a?.id !== rowId) as any,
        );
      }
    },
    [isEditDealContext, dealId, setAssets, setTenants],
  );

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: "",
        field: "included",
        width: 50,
        editable: false,
        cellRenderer: (params: ICellRendererParams) => {
          const isChecked = isEditDealContext
            ? params.data?.deal?.id === dealId!
            : true;

          return (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(e.target.checked, params.data?.id);
              }}
            />
          );
        },
        onCellClicked: (params: any) => {
          if (params.column.getColId() === "included") {
            const currentChecked = isEditDealContext
              ? params.data?.deal?.id === dealId!
              : true;
            handleCheckboxChange(!currentChecked, params.data?.id);
          }
        },
      },
      {
        headerName: "",
        field: "delete",
        width: 50,
        editable: false,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const rowId = params.data?.id as number | string | undefined;
                if (rowId !== null && rowId !== undefined) {
                  handleDeleteRowById(rowId);
                }
              }}
              sx={{ padding: "2px" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
        },
      },

      createSingleSelectColumn(
        "deal",
        "Deal",
        deals || [],
        "deal_name",
        150,
        true,
      ),
      // Accordion column - use same format as sector select
      ...(selectableAccordions.length > 0
        ? [
            {
              ...createSingleSelectColumn(
                "accordion",
                "Addon",
                selectableAccordions,
                "accordion_name",
                150,
                true,
                true,
              ),
              valueGetter: (params: any) => {
                if (!params.data) return { id: null, label: "(None)" };
                const assetId = params.data.id;
                if (!assetId || typeof assetId !== "number")
                  return { id: null, label: "(None)" };

                // Always prioritize accordion_id from asset data (source of truth)
                const accordionId = params.data.accordion_id;

                // If accordion_id is explicitly null, return null option object
                if (accordionId === null) {
                  return { id: null, label: "(None)" };
                }

                // If accordion_id exists, find the accordion object
                if (accordionId !== undefined && accordionId !== null) {
                  const accordion = selectableAccordions.find(
                    (acc: any) => acc.id === accordionId,
                  );
                  return accordion
                    ? { id: accordion.id, label: accordion.accordion_name }
                    : { id: null, label: "(None)" };
                }

                // No accordion assigned - return null option object
                return { id: null, label: "(None)" };
              },
              valueSetter: (params: any) => {
                // Handle null values - when "(None)" is selected, newValue will be { id: null, label: '(None)' }
                if (
                  params.newValue === null ||
                  params.newValue === undefined ||
                  (params.newValue &&
                    typeof params.newValue === "object" &&
                    params.newValue.id === null)
                ) {
                  if (params.data) {
                    // Explicitly set to null (not undefined)
                    params.data.accordion = null;
                    params.data.accordion_id = null;
                  }
                  // Update tracking map immediately
                  const assetId = params.data?.id;
                  if (assetId && typeof assetId === "number") {
                    setAccordionAssignments((prev) => {
                      const updated = new Map(prev);
                      updated.set(assetId, null);
                      return updated;
                    });
                  }
                  return true;
                }
                // Handle object values
                if (
                  params.newValue &&
                  typeof params.newValue === "object" &&
                  params.newValue.id !== null &&
                  params.newValue.id !== undefined
                ) {
                  if (params.data) {
                    // Find the full accordion object from selectableAccordions
                    const fullAccordion = selectableAccordions.find(
                      (acc: any) => acc.id === params.newValue.id,
                    );
                    params.data.accordion = fullAccordion || params.newValue;
                    params.data.accordion_id = params.newValue.id;
                  }
                  // Update tracking map immediately
                  const assetId = params.data?.id;
                  if (assetId && typeof assetId === "number") {
                    setAccordionAssignments((prev) => {
                      const updated = new Map(prev);
                      updated.set(assetId, params.newValue.id);
                      return updated;
                    });
                  }
                  return true;
                }
                return false;
              },
            } as ColDef,
          ]
        : [
            {
              field: "accordion",
              headerName: "Addon",
              editable: false,
              width: 150,
              valueGetter: (params: any) => {
                const assetId = params.data?.id;
                if (
                  !assetId ||
                  typeof assetId !== "number" ||
                  !accordions.length
                )
                  return null;

                // Find first matching accordion (asset can only be in one)
                const matchingAccordion = accordions.find(
                  (accordion) =>
                    accordion.asset_ids &&
                    Array.isArray(accordion.asset_ids) &&
                    accordion.asset_ids.includes(assetId),
                );

                return matchingAccordion
                  ? matchingAccordion.accordion_name
                  : null;
              },
              valueFormatter: (params: any) => {
                return params.value || "-";
              },
            },
          ]),

      {
        headerName: "Existing?",
        field: "is_existing",
        editable: false,
        width: 95,
        valueGetter: (params: any) => {
          const id = params?.data?.id;
          const isPersisted =
            id !== null && id !== undefined && typeof id === "number";
          return isPersisted ? "Existing" : "New";
        },
        cellStyle: (params: any) => {
          const value = params?.value;
          const isExisting = value === "Existing";
          return {
            backgroundColor: isExisting ? "#fff8e1" : "#e8f5e9", // amber / green
            color: "#1f1f1f",
            textAlign: "center",
          };
        },
      },
      { field: "asset_name", headerName: "Asset", editable: true, width: 150 },
      {
        field: "address_1",
        headerName: "Address 1",
        editable: true,
        width: 150,
      },
      {
        field: "address_2",
        headerName: "Address 2",
        editable: true,
        width: 100,
      },
      { field: "city", headerName: "City", editable: true, width: 100 },
      createObjectArraySelectColumn(
        "sector",
        "Sector",
        sectors || [],
        "sector",
        100,
        true,
        "primary_sector_id",
      ),
      createObjectArraySelectColumn(
        "country",
        "Country",
        countries || [],
        "nicename",
        100,
        true,
        "country_id",
      ),
      createStringSelectColumn(
        "currency",
        "Currency",
        currencies || [],
        100,
        true,
      ),
      {
        field: "valuation_amount",
        headerName: "Valuation",
        editable: true,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "valuation_date",
        headerName: "Valuation Date",
        editable: true,
        width: 100,
        valueFormatter: dateFormatter,
      },
      {
        field: "acquisition_price",
        headerName: "Acquisition Price",
        editable: true,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "acquisition_date",
        headerName: "Acquisition Date",
        editable: true,
        width: 100,
        valueFormatter: dateFormatter,
      },
      { field: "epc_rating", headerName: "EPC", editable: true, width: 100 },
      {
        field: "year_built",
        headerName: "Year Built",
        editable: true,
        width: 100,
      },
      {
        field: "year_refurbished",
        headerName: "Year refurbished",
        editable: true,
        width: 100,
      },
      {
        field: "count_buildings",
        headerName: "No. of Buildings",
        editable: true,
        width: 100,
      },
      {
        field: "count_floors",
        headerName: "No. of Floors",
        editable: true,
        width: 100,
      },
      {
        field: "count_boxes",
        headerName: "No. of Boxes",
        editable: true,
        width: 100,
      },
      {
        field: "count_rooms",
        headerName: "No. of Rooms",
        editable: true,
        width: 100,
      },
      {
        field: "count_units",
        headerName: "No. of Units",
        editable: true,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "noi",
        headerName: "NOI",
        editable: true,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "opex_annual",
        headerName: "OPEX (Annual)",
        editable: true,
        width: 100,
        valueFormatter: numberFormatter,
      },
      {
        field: "capex_total",
        headerName: "Capex Total",
        editable: true,
        width: 100,
        valueFormatter: numberFormatter,
      },
    ];
  }, [
    deals,
    countries,
    sectors,
    currencies,
    assets,
    isEditDealContext,
    dealId,
    selectableAccordions,
    accordionAssignments,
    accordions,
  ]);

  const onCellValueChanged = (event: any) => {
    const { colDef, newValue, data } = event;
    const field = colDef.field;
    const rowId = data?.id as number | string | undefined;
    if (rowId === null || rowId === undefined) return;

    // Handle accordion assignment changes - update accordion_id on asset
    if (field === "accordion") {
      // Handle null values - when "(None)" is selected, newValue will be { id: null, label: '(None)' }
      const accordionId =
        newValue === null ||
        newValue === undefined ||
        (newValue && typeof newValue === "object" && newValue.id === null)
          ? null
          : typeof newValue === "object" && newValue.id
            ? newValue.id
            : newValue;

      // Find the full accordion object if we have an ID
      const fullAccordion =
        accordionId !== null
          ? selectableAccordions.find((acc: any) => acc.id === accordionId)
          : null;

      setAssets(
        (prev) =>
          (prev as any[]).map((a: any) =>
            a?.id === rowId
              ? { ...a, accordion: fullAccordion, accordion_id: accordionId }
              : a,
          ) as any,
      );

      // Update tracking map
      if (typeof rowId === "number") {
        setAccordionAssignments((prev) => {
          const updated = new Map(prev);
          updated.set(rowId, accordionId);
          return updated;
        });
      }
      return;
    }

    // Snapshot the whole row so valueSetter side-effects (e.g. primitive currency,
    // primary_sector_id) flow into shared deal state and reach any subsequent PUT.
    setAssets(
      (prev) =>
        (prev as any[]).map((a: any) =>
          a?.id === rowId ? { ...data } : a,
        ) as any,
    );
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      asset_name: "",
      address_1: "",
      address_2: "",
      city: "",
      currency: "",
      ...(isEditDealContext ? { deal: { id: dealId! } } : {}),
    };
    setAssets((prev) => [...(prev as any[]), newRow as any] as any);
  };

  return (
    <>
      {renderSubTitle("Assets")}

      <div
        className="ag-theme-alpine"
        style={{ height: 320, width: "100%", marginTop: 10 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={assets}
          columnDefs={columnDefs}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          defaultColDef={{ flex: 0, resizable: true }}
          rowHeight={30}
          getRowId={({ data }) => {
            // All rows should now have unique IDs
            return (
              data?.id?.toString() ||
              `fallback-${Math.random().toString(36).substr(2, 9)}`
            );
          }}
        />
      </div>
      <Box display="flex" gap={2} mb={1} mt={2}>
        <CustomButton
          handleButtonPress={handleAddRow}
          isButtonDisabled={false}
          solid={false}
          text="Add Row"
        ></CustomButton>
      </Box>
    </>
  );
};

// Memoized so parent forms (e.g. CreateDealUpdate) can re-render on each
// keystroke without re-rendering this component.
export default memo(ExtractedAssetsTable);
